"use client";

import React from "react";
import { Checkbox as AntCheckbox, ConfigProvider } from "antd";
import type { CheckboxProps as AntCheckboxProps } from "antd";
import { orionTheme } from "../theme/orion-theme";
import clsx from "clsx";
import "../styles/checkbox.css";

export interface CheckboxProps extends AntCheckboxProps {
  /**
   * Additional CSS classes (supports Tailwind overrides)
   */
  className?: string;
}

/**
 * Checkbox component - Ant Design Checkbox with OpenStore Orion theming
 *
 * This component provides FULL access to all Ant Design Checkbox props while
 * automatically applying the OpenStore Orion theme and Figma design specifications.
 *
 * Features:
 * - Figma design specifications (20x20px, specific colors, checkmark styling)
 * - Automatic theming
 * - Tailwind class override support
 * - ALL Ant Design Checkbox features and props
 *
 * @example
 * ```tsx
 * // Basic checkbox with Orion theming
 * <Checkbox>Label</Checkbox>
 *
 * // All Ant Design Checkbox props work
 * <Checkbox
 *   checked={isChecked}
 *   onChange={handleChange}
 *   indeterminate={isIndeterminate}
 *   disabled={isDisabled}
 *   autoFocus
 *   className="custom-checkbox"
 * />
 *
 * // Checkbox group
 * <Checkbox.Group
 *   options={['Option 1', 'Option 2', 'Option 3']}
 *   value={selectedValues}
 *   onChange={handleGroupChange}
 * />
 * ```
 */
export const Checkbox: React.FC<CheckboxProps> = ({ className, ...props }) => {
  return (
    <ConfigProvider theme={orionTheme}>
      <AntCheckbox className={clsx("orion-checkbox", className)} {...props} />
    </ConfigProvider>
  );
};

Checkbox.displayName = "Checkbox";

// Export convenience components
export const CheckboxGroup = AntCheckbox.Group;
